<template>
  <div class="buyCenter">
    <div class="headImg">
      <div class="headContent">
        <span class="headText">{{ $t("Buy.priceSectionTitle") }}</span>

        <!-- 4 大家族卡片（横排，默认选中第一个） -->
        <div class="familyCards">
          <div
            v-for="item in productTypes"
            :key="item.id"
            class="familyCard"
            :class="{ active: activeFamilyId === item.id }"
            @click="selectFamily(item)"
          >
            <div class="familyIcon">
              <img :src="item.icon" :alt="item.familyCode" />
            </div>
            <div class="familyName">{{ item.familyCode }}</div>
            <div class="familySubName">{{ item.familyName }}</div>
          </div>
        </div>

        <!-- 选中家族下的产品列表（每行一个） -->
        <div class="productListSection">
          <div v-if="productLoading" class="loading-tip">加载中...</div>
          <template v-else>
            <div
              v-for="prod in loopSoftList"
              :key="prod.id"
              :ref="`productItem-${prod.id}`"
              class="productItem"
              :class="{ active: expandedProductId === prod.id }"
            >
              <!-- 产品行：用 table 布局，版本标签列与下方功能对比表列对齐 -->
              <div class="productHeader" @click="toggleProduct(prod)">
                <table class="productHeaderTable">
                  <colgroup>
                    <col class="col-name" />
                    <col v-for="col in familyVersionCols" :key="'ph-col-' + col.type" />
                  </colgroup>
                  <tbody>
                    <tr>
                      <td class="productNameCell">
                        <h3 class="productName">{{ language === 'en' && prod.nameEn ? prod.nameEn : prod.name }}</h3>
                      </td>
                      <td
                        v-for="col in familyVersionCols"
                        :key="'ph-' + col.type"
                        class="versionTagCell"
                      >
                        <span
                          v-if="getProdVersionByType(prod, col.type)"
                          class="versionTag"
                          :class="versionTagClass(getProdVersionByType(prod, col.type))"
                          @click.stop="handleVersionBuy(getProdVersionByType(prod, col.type), prod)"
                        >
                          {{ getPlanName(getProdVersionByType(prod, col.type)) }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- 展开内容：功能对比表 -->
              <div v-if="expandedProductId === prod.id" class="productBody">
                <div v-if="prod._loading" class="loading-tip">加载中...</div>
                <template v-else>
                  <!-- 功能对比表（紧贴产品行下方，无标题/无边框） -->
                  <div
                    v-if="hasComparisonFor(prod)"
                    ref="comparisonSection"
                    class="comparisonTable"
                  >
                    <table>
                      <colgroup>
                        <col class="col-name" />
                        <col v-for="col in familyVersionCols" :key="'ct-col-' + col.type" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th></th>
                          <th
                            v-for="col in familyVersionCols"
                            :key="'ct-th-' + col.type"
                            @click="getProdVersionByType(prod, col.type) && handleVersionBuy(getProdVersionByType(prod, col.type), prod)"
                          >
                            <div class="verPrice" v-if="getProdVersionByType(prod, col.type)">
                              <template v-if="isCustomPlan(getProdVersionByType(prod, col.type))">
                                <span class="contactTag">{{ $t("Buy.contactSales") }}</span>
                              </template>
                              <template v-else>
                                <div class="priceBox">
                                  <span v-if="getProdVersionByType(prod, col.type).weekprice" class="verPriceSymbol">¥</span>
                                  <span class="priceNum">{{ getProdVersionByType(prod, col.type).weekprice || 0 }}</span>
                                  <span v-if="getProdVersionByType(prod, col.type).weekprice" class="verPriceUnit">{{ $t("Buy.perWeek") }}</span>
                                </div>
                              </template>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(row, index) in flattenedComparisonFor(prod)"
                          :key="'row-' + index"
                          :class="{ 'category-row': row.values.every((val) => !val) }"
                        >
                          <td class="indent-cell">
                            <span class="text-content">{{ row.title }}</span>
                          </td>
                          <td v-for="(val, idx) in row.values" :key="idx">
                            <template v-if="row.values.some((v) => v)">
                              <i
                                v-if="val == '✕' || val == 'X' || val == 'x' || val == '×'"
                                class="el-icon-close icon-close"
                              ></i>
                              <i
                                v-else-if="val == '√' || val == '✓'"
                                class="el-icon-check icon-check"
                              ></i>
                              <span v-else class="text-content">{{ val || "-" }}</span>
                            </template>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </template>
              </div>
            </div>

            <div v-if="!productLoading && !loopSoftList.length" class="empty-tip">暂无产品</div>
          </template>
        </div>
      </div>
    </div>

    <!-- 产品详情模块 -->
    <div id="productMes" class="productMes">
      <div class="tabTitle">
        <p>{{ $t("Buy.SystemConfiguration") }}</p>
      </div>
      <div class="productMes">
        <div class="systemReqStyle">
          <div class="systemContainer">
            <!-- 顶部标题区域 -->
            <div class="systemHeader">
              <div class="headerContent">
                <h2 class="systemMainTitle">
                  <span class="titleDesc">{{
                    requireContentList.leftDes
                  }}</span>
                  <span class="titleName">{{ requireContentList.leftH }}</span>
                </h2>
                <p class="systemSubtitle">
                  {{ $t("Buy.operatingSystem") }}：{{
                    requireContentList.rightP
                  }}
                </p>
              </div>
            </div>

            <!-- 配置内容区域 -->
            <div class="configContent">
              <!-- 入门配置 -->
              <div
                v-for="(config, i) in requireContentList.optional"
                :key="'optional-' + i"
                class="configSection"
              >
                <div class="sectionTitle">
                  <span class="titleText">{{ $t("Buy.entryConfig") }}</span>
                </div>
                <div class="configTable">
                  <div
                    class="configRow"
                    v-for="(item, index) in config.leftP || []"
                    :key="'leftP-' + index"
                  >
                    <div class="configLabel">
                      {{ item.title }}
                    </div>
                    <div class="configValue">{{ item.content }}</div>
                  </div>
                </div>
              </div>

              <!-- 推荐配置 -->
              <div
                v-for="(config, i) in requireContentList.optionalRight"
                :key="'right-' + i"
                class="configSection recommended"
              >
                <div class="sectionTitle">
                  <span class="titleText">{{ $t("Buy.recommendConfig") }}</span>
                  <span class="recommendTag">{{ $t("Buy.recommend") }}</span>
                </div>
                <div class="configTable">
                  <div
                    class="configRow"
                    v-for="(item, index) in config.leftP || []"
                    :key="'leftP-' + index"
                  >
                    <div class="configLabel">
                      {{ item.title }}
                    </div>
                    <div class="configValue">{{ item.content }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 订阅指南模块 -->
    <div class="subscribeGuideSection animate-on-scroll">
      <h2 class="guideTitle">{{ $t("Buy.subscribeGuide") }}</h2>
      <div class="guideContainer">
        <!-- 左侧导航 -->
        <div class="guideNav">
          <el-menu
            :default-active="currentGuideId"
            class="guideMenu"
            unique-opened
            @select="handleGuideSelect"
          >
            <guide-menu-node
              v-for="item in guideMenuItems"
              :key="item.id"
              :node="item"
              :depth="0"
            />
          </el-menu>
        </div>

        <!-- 右侧内容 -->
        <div class="guideContent">
          <div class="guideDetail" v-html="guideDetailHtml"></div>
        </div>
      </div>
    </div>

    <!-- 订阅弹框 -->
    <SubscribeDialog
      :visible.sync="subscribeDialogVisible"
      :product-info="currentSubscribeProduct"
      @subscribe="handleSubscribeConfirm"
      @add-cart="handleAddCart"
    />

    <!-- 订单确认弹框 -->
    <OrderConfirmDialog
      :visible.sync="orderConfirmDialogVisible"
      :order-info="currentOrderInfos"
      @cancel="subscribeDialogVisible = true"
    />

    <!-- 购物车 -->
    <ShoppingCartDialog
      :visible.sync="shoppingCartDialogVisible"
      :items="currentOrderInfo"
      @close="shoppingCartDialogVisible = false"
      @settle="handleCartSettle"
    />
    <!-- 与右侧悬浮“联系客服”使用同一个客服弹窗组件 -->
    <ChatDialog ref="chatDialog" />
  </div>

</template>

<script>
import {
  getAllProduction,
  getProductDetailList,
  getProductFuntionList,
  commonQuestion,
  productionVersion,
  getTreeByProductId,
} from "@/assets/API/pay";
import { GetUserMesByToken } from "@/assets/API/login";

import {
  family,
  Selector,
  productComputerConfig,
  subscribeGuide,
  subscribeGuideItem,
} from "@/assets/API/AllProducts.js";
import SubscribeDialog from "./components/SubscribeDialog.vue";
import OrderConfirmDialog from "./components/OrderConfirmDialog.vue";
import ShoppingCartDialog from "./components/ShoppingCartDialog.vue";

// 右侧悬浮“联系客服”使用的也是这个组件
import ChatDialog from "@/views/Home/ChatDialog.vue";

import pdf from "vue-pdf";
import define from "@/utils/define";

// ==================== 常量配置 ====================
const PRODUCT_COLORS = ["#2b85e5", "#ff8d1a", "#9b59b6", "#1abc9c", "#e74c3c"];

const PLAN_TYPE = {
  FREE: 0,
  SUBSCRIBE: 1,
  CUSTOM: 99,
};

// 订阅指南树形菜单节点（递归渲染 el-submenu / el-menu-item）
const GuideMenuNode = {
  name: "GuideMenuNode",
  props: {
    node: { type: Object, default: () => ({}) },
    depth: { type: Number, default: 0 },
  },
  render(h) {
    const children = this.node.children || [];
    const label = this.node.label;
    if (children.length) {
      return h(
        "el-submenu",
        { props: { index: String(this.node.id) } },
        [
          h("template", { slot: "title" }, [label]),
          ...children.map((child) =>
            h("GuideMenuNode", {
              props: { node: child, depth: this.depth + 1 },
              key: child.id,
            })
          ),
        ]
      );
    }
    // 顶级叶子节点加标记类，与顶级分组标题对齐（padding 24px）
    const cls =
      this.depth === 0 ? { class: "guide-top-item" } : {};
    return h(
      "el-menu-item",
      { props: { index: String(this.node.id) }, ...cls },
      [label]
    );
  },
};

export default {
  name: "BuyPage",
  components: {
    pdf,
    SubscribeDialog,
    OrderConfirmDialog,
    ShoppingCartDialog,
    ChatDialog,
    GuideMenuNode,
  },

  data() {
    return {
      define,
      language: this.$i18n.locale,

      // 分类相关
      currentCategory: "all",
      categoryTabs: [],

      // 4 大家族：当前选中的家族 id
      activeFamilyId: "",
      productLoading: false,
      // 家族版本列模板：该家族所有产品版本的并集（按类型固定排序），用于固定列位
      familyVersionCols: [],
      // 产品手风琴：当前展开的产品 id（null = 全部收起）
      expandedProductId: null,
      pendingExpandId: null,
      pendingFamilyCode: "",

      // 产品相关
      activeID: "",
      productTypes: [],
      loopSoftList: [],
      choosedSoft: {},
      chooseFamily: null,
      bgImg: "",

      // 轮播相关
      currentProductId: '',
      currentIndex: 0,
      slideAnimating: false,
      CARD_STEP: 324,

      // 定价方案
      selectedPlan: null,
      pricingList: [],

      // 功能对比
      comparisonList: [],

      // 系统配置
      requireContentList: {},

      // FAQ
      faqList: [],

      // 详情相关
      detailList: [],
      functionList: [],

      // PDF 相关
      currentGuideId: "1-1",
      guideMenuItems: [],
      guideDetailHtml: "",

      // 订阅弹框相关
      subscribeDialogVisible: false,
      currentSubscribeProduct: {},
      // 订单确认弹框相关
      orderConfirmDialogVisible: false,
      currentOrderInfo: {},
      currentOrderInfos: [],

      shoppingCartDialogVisible: false,
    };
  },

  computed: {
    slideOffset() {
      return -this.currentIndex * this.CARD_STEP;
    },

    // 是否有功能对比数据（基于当前选中家族的版本）
    hasComparison() {
      return this.flattenedComparisonList.length > 0;
    },

    // 扁平化功能对比列表（基于当前选中家族的版本）
    flattenedComparisonList() {
      const result = [];
      const versionList = this.pricingList;

      const flatten = (nodes, level = 0) => {
        if (!Array.isArray(nodes) || !nodes.length) return;

        nodes.forEach((node) => {
          const title = this.getLocalizedField(node, "title");
          const valueJson = this.getLocalizedField(node, "valueJson");
          const values = this.parseValueJson(valueJson, versionList);
          const hasChildren =
            Array.isArray(node.children) && node.children.length > 0;
          const isCategory = hasChildren && values.every((v) => !v);

          result.push({
            id: node.id,
            title,
            values: isCategory
              ? new Array(versionList.length).fill("")
              : values,
            isCategory,
            level,
          });

          if (hasChildren) {
            flatten(node.children, level + 1);
          }
        });
      };

      flatten(this.comparisonList);
      return result;
    },
  },

  watch: {
    $route: {
      immediate: true,
      handler(route) {
        if (route.path === "/Buy") {
          // 深链兼容：带 productId 时，待 productTypes 就绪后自动展开对应产品
          const { productId, familyCode } = route.query;
          if (productId) {
            this.activeID = productId;
            this.pendingExpandId = productId;
            this.pendingFamilyCode = familyCode || "";
            if (this.productTypes.length) {
              const targetFamily = this.productTypes.find(
                (item) => item.familyCode === this.pendingFamilyCode
              );
              if (targetFamily) this.selectFamily(targetFamily);
            }
          }
        }
      },
    },
  },

  mounted() {
    this.init();
    this.getsubscribeGuide();
    this.$nextTick(() => this.observeSections());
  },

  methods: {
    getrequireContentList(productId) {
      productComputerConfig(productId)
        .then((res) => {
          const product = this.productTypes.find((p) => p.id === productId);
          if (res.code === 200 && res.data?.length > 0) {
            const config = res.data[0];
            this.requireContentList = {
              leftH: product.familyCode,
              leftDes: this.getLocalizedField(product, "familyName"),
              rightP: this.getLocalizedField(config, "optSystem"),
              optional: [
                {
                  leftH: "入门配置",
                  leftP: res.data
                    .filter((item) => item.type == 1)
                    .map((item) => ({
                      ...item,
                      title: this.getLocalizedField(item, "title"),
                      content: this.getLocalizedField(item, "content"),
                    })),
                },
              ],
              optionalRight: [
                {
                  leftH: "推荐配置",
                  leftP: res.data
                    .filter((item) => item.type == 2)
                    .map((item) => ({
                      ...item,
                      title: this.getLocalizedField(item, "title"),
                      content: this.getLocalizedField(item, "content"),
                    })),
                },
              ],
            };
          } else {
            this.requireContentList = {
              leftH: product.familyCode,
              leftDes: product.familyName,
            };
          }
        })
        .catch((e) => {
          console.error("获取系统配置失败:", e);
        });
    },
    async getsubscribeGuide() {
      try {
        const res = await subscribeGuide();
        if (res.code === 200 && Array.isArray(res.data) && res.data.length) {
          // 后端已返回树形结构，保留层级并本地化标题
          const localize = (nodes) =>
            (nodes || []).map((node) => ({
              ...node,
              label: this.getLocalizedField(node, "title"),
              children: localize(node.children),
            }));
          this.guideMenuItems = localize(res.data);
          // 取第一个叶子节点作为默认选中
          const firstLeafId = this.findFirstLeafId(this.guideMenuItems);
          this.currentGuideId = firstLeafId;
          this.fetchGuideDetail(this.currentGuideId);
        }
      } catch (e) {
        console.error("获取订阅指南失败:", e);
      }
    },
    // 递归查找树中第一个叶子节点 id（作为默认选中项）
    findFirstLeafId(nodes) {
      for (const node of nodes || []) {
        if (node.children && node.children.length) {
          const id = this.findFirstLeafId(node.children);
          if (id) return id;
        } else {
          return String(node.id);
        }
      }
      return null;
    },
    async fetchGuideDetail(id) {
      if (!id) {
        this.guideDetailHtml = "";
        return;
      }
      try {
        const res = await subscribeGuideItem(id);
        if (res.code === 200 && res.data) {
          this.guideDetailHtml = this.getLocalizedField(res.data[0], "content");
        } else {
          this.guideDetailHtml = `<p>${this.$t("Buy.noContent")}</p>`;
        }
      } catch (e) {
        console.error("获取订阅指南详情失败:", e);
        this.guideDetailHtml = `<p>${this.$t("Buy.loadFailed")}</p>`;
      }
    },
    observeSections() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1 }
      );
      this.$nextTick(() => {
        document.querySelectorAll('.animate-on-scroll').forEach((el) => {
          observer.observe(el);
        });
      });
    },
    // ==================== 初始化 ====================
    init() {
      this.getFamily();
      this.categoryTabsInit();
      this.handleGuideSelect("1-1");
    },

    // ==================== 工具方法 ====================
    getLocalizedField(obj, field) {
      if (!obj) return "";
      const enField = `${field}En`;
      return this.language === "zh" ? obj[field] : obj[enField] || obj[field];
    },

    parseImageUrl(jsonStr) {
      if (!jsonStr) return "";
      try {
        const parsed = JSON.parse(jsonStr);
        return parsed[0]?.url ? this.define.APIURl + parsed[0].url : "";
      } catch {
        return "";
      }
    },

    parseJson(jsonStr, defaultValue = []) {
      if (!jsonStr) return defaultValue;
      try {
        const parsed = JSON.parse(jsonStr);
        return Array.isArray(parsed) ? parsed : Object.values(parsed);
      } catch {
        return defaultValue;
      }
    },

    parseValueJson(valueJson, versionList) {
      if (!valueJson || !Array.isArray(versionList) || versionList.length === 0)
        return [];
      try {
        const parsed = JSON.parse(valueJson);
        return versionList.map((version) => {
          const key = String(version.type ?? "");
          return parsed[key] || "";
        });
      } catch {
        return new Array(versionList.length).fill("");
      }
    },

    // ==================== 定价方案相关 ====================
    isCustomPlan(plan) {
      return plan.type == PLAN_TYPE.CUSTOM;
    },

    hasFeatures(plan) {
      return Array.isArray(plan.features) && plan.features.length > 0;
    },

    getPlanBtnText(plan) {
      const textMap = {
        [PLAN_TYPE.FREE]: this.$t("Buy.freeUse"),
        [PLAN_TYPE.CUSTOM]: this.$t("Buy.contactSales"),
      };
      return textMap[plan.type] || this.$t("Buy.buyNow");
    },

    getPlanName(plan) {
      const nameMap = {
        0: { zh: "免费版", en: "Free Version" },
        1: { zh: "订阅版", en: "Subscription" },
        99: { zh: "定制版", en: "Custom" },
      };
      const name = nameMap[plan.type];
      if (!name) return plan.name;
      return this.language === 'en' ? name.en : name.zh;
    },

    handlePlanAction(plan) {
      const actions = {
        [PLAN_TYPE.FREE]: () => this.toBuy(plan, "free"),
        // plan.exeCmd && window.open(plan.exeCmd, "_blank"),
        [PLAN_TYPE.CUSTOM]: () => this.contactUs(),
      };
      (actions[plan.type] || (() => this.toBuy(plan)))();
    },

    selectPlan(planId) {
      this.selectedPlan = planId;
    },

    // ==================== 功能对比值显示 ====================
    getValueDisplay(val) {
      if (val === "✕" || val === "X" || val === "x" || val === "")
        return { type: "icon", icon: "el-icon-close", class: "icon-close" };
      if (val === "√" || val === "✓")
        return { type: "icon", icon: "el-icon-check", class: "icon-check" };
      return { type: "text", value: val };
    },

    getValueClass(val) {
      if (val === "✕" || val === "X" || val === "") return "icon-close";
      if (val === "√" || val === "✓") return "icon-check";
      return "text-content";
    },

    // ==================== API 请求 ====================
    async categoryTabsInit() {
      try {
        const res = await Selector();
        if (res.code === 200 && res.data?.list) {
          this.categoryTabs = [
            { fullName: "全部", fullNameEn: "All", id: "all" },
            ...res.data.list,
          ];
        }
      } catch (e) {
        console.error("获取分类失败:", e);
      }
    },

    async getFamily(id = null) {
      try {
        const res = await family({
          seriesId: id,
          showSubscribe: "Y",
        });
        if (res.code === 200 && res.data?.length > 0) {
          this.productTypes = res.data.map((item) => ({
            ...item,
            title: this.getLocalizedField(item, "title"),
            familyCode: item.familyCode,
            familyName: this.getLocalizedField(item, "familyName"),
            content: this.getLocalizedField(item, "content"),
            icon: this.parseImageUrl(item.icon),
            fileData: this.parseImageUrl(item.fileData),
            bgImg: this.parseImageUrl(item.bgImg),
            introduceImg: this.parseImageUrl(item.introduceImg),
          }));

          this.bgImg = this.productTypes[0]?.bgImg || "";

          // 默认选中第一个家族，加载其产品列表
          // pendingExpandId（来自 URL ?productId=）在 loadFamilyProducts 中匹配产品并自动展开
          if (this.productTypes.length) {
            const targetFamily = this.productTypes.find(
              (item) => item.familyCode === this.pendingFamilyCode
            );
            this.selectFamily(targetFamily || this.productTypes[0]);
          }
        }
      } catch (e) {
        console.error("获取产品类型失败:", e);
      }
    },

    // ==================== 家族切换：加载该家族下的产品 ====================
    selectFamily(family) {
      if (!family) return;
      this.activeFamilyId = family.id;
      this.activeID = family.id;
      this.chooseFamily = family;
      this.expandedProductId = null; // 切换家族时收起展开
      this.familyVersionCols = [];   // 切换家族时清空列模板（loadFamilyProducts 会重算）
      this.loadFamilyProducts(family);
      this.getrequireContentList(family.id);
    },

    // 加载某家族下的产品列表，并预取每个产品的版本（用于产品行右侧版本标签）
    async loadFamilyProducts(family) {
      this.productLoading = true;
      let deepLinkProductId = null;
      try {
        const res = await getAllProduction({
          familyCode: family.familyCode,
          saletype: "online",
          enabledmark: 1,
          sidx: "sortcode",
          sort: "asc",
        });
        const list = (res.data?.list || []).map((item) => ({
          ...item,
          icon: this.parseImageUrl(item.icon),
          // 每个产品独立的版本/对比缓存（功能对比懒加载）
          _pricingList: [],
          _comparisonList: [],
          _loaded: false,
          _loading: false,
        }));
        this.loopSoftList = list;

        // 默认上下文指向第一个产品（供 toBuy 等兜底使用）
        if (list.length > 0) {
          this.choosedSoft = list[0];
          this.currentProductId = list[0].id;
        }

        // 预加载每个产品的版本（productionVersion），用于产品行右侧版本标签
        await Promise.all(
          list.map((prod) => this.loadProductVersions(prod))
        );

        // 计算该家族所有产品版本的并集，按类型固定排序（免费版→订阅版→定制版）
        // 作为固定列模板，保证每个产品行/对比表的列位一致（缺的版本空着）
        this.familyVersionCols = this.buildFamilyVersionCols(list);

        // 深链：若 URL 带 productId 且属于该家族，自动展开对应产品
        if (this.pendingExpandId) {
          const matched = list.find((p) => p.id === this.pendingExpandId);
          if (matched) {
            this.toggleProduct(matched);
            deepLinkProductId = matched.id;
          }
          this.pendingExpandId = null;
        }
      } catch (e) {
        console.error("获取家族产品失败:", e);
      } finally {
        this.productLoading = false;
        if (deepLinkProductId) {
          this.$nextTick(() => this.scrollToProduct(deepLinkProductId));
        }
      }
    },

    // 加载单个产品的版本列表（pricingList），结果挂到 prod._pricingList
    async loadProductVersions(prod) {
      if (!prod || (prod._pricingList && prod._pricingList.length)) return;
      try {
        const res = await productionVersion({
          currentPage: 1,
          pageSize: 20,
          productId: prod.id,
          enabledmark: 1,
        });
        if (res.code === 200 && res.data?.records) {
          const pricingList = res.data.records.map((item) => ({
            ...item,
            icon: this.parseImageUrl(item.icon),
            img: this.parseImageUrl(item.img),
            features: this.parseJson(item.coreFuc),
            weekprice: Math.max(item.weekprice || 0, 0),
          }));
          this.$set(prod, "_pricingList", pricingList);
        }
      } catch (e) {
        console.error("获取产品版本失败:", e);
      }
    },

    // 计算家族版本列模板：所有产品版本的并集，按类型固定排序（免费→订阅→定制）
    // 返回 [{ type, sample }]，type 为版本类型，sample 为该类型的一个示例（取名称用）
    buildFamilyVersionCols(list) {
      const order = [0, 1, 99]; // 免费版=0，订阅版=1，定制版=99
      const map = {}; // type -> sample version
      (list || []).forEach((prod) => {
        (prod._pricingList || []).forEach((ver) => {
          const t = Number(ver.type);
          if (map[t] === undefined) map[t] = ver;
        });
      });
      // 按 order 顺序输出存在的类型；order 之外的类型按数字大小排在后面
      const present = Object.keys(map).map(Number);
      const ordered = order.filter((t) => map[t] !== undefined);
      const extra = present.filter((t) => !order.includes(t)).sort((a, b) => a - b);
      return [...ordered, ...extra].map((t) => ({ type: t, sample: map[t] }));
    },

    // 按版本类型取某产品的版本（没有则返回 null，用于占位空列）
    getProdVersionByType(prod, type) {
      if (!prod || !prod._pricingList) return null;
      return (
        prod._pricingList.find((v) => Number(v.type) === Number(type)) || null
      );
    },

    // 产品手风琴：展开/收起，展开时懒加载功能对比
    toggleProduct(prod) {
      if (!prod) return;
      if (this.expandedProductId === prod.id) {
        this.expandedProductId = null;
        return;
      }
      this.expandedProductId = prod.id;
      this.choosedSoft = prod; // 更新当前上下文产品
      this.ensureProductComparison(prod);
    },

    scrollToProduct(productId) {
      const refs = this.$refs[`productItem-${productId}`];
      const element = Array.isArray(refs) ? refs[0] : refs;
      if (!element) return;
      const header = document.querySelector(".summitek-header");
      const headerBottom = header
        ? Math.max(0, header.getBoundingClientRect().bottom)
        : 80;
      const top =
        element.getBoundingClientRect().top +
        window.pageYOffset -
        headerBottom -
        16;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    },

    // 懒加载某产品的功能对比，已加载则跳过
    async ensureProductComparison(prod) {
      if (!prod || prod._loaded || prod._loading) return;
      this.$set(prod, "_loading", true);
      try {
        const res = await getTreeByProductId(prod.id);
        if (res.code === 200 && res.data) {
          this.$set(prod, "_comparisonList", res.data);
        }
        this.$set(prod, "_loaded", true);
      } catch (e) {
        console.error("获取功能对比失败:", e);
      } finally {
        this.$set(prod, "_loading", false);
      }
    },

    // 该产品是否有功能对比数据
    hasComparisonFor(prod) {
      return this.flattenedComparisonFor(prod).length > 0;
    },

    // 该产品的扁平化功能对比列表（按 familyVersionCols 列模板对齐）
    flattenedComparisonFor(prod) {
      const result = [];
      if (!prod) return result;
      const comparisonList = prod._comparisonList || [];
      const cols = this.familyVersionCols || [];
      if (!cols.length || !comparisonList.length) return result;

      // 每列对应的产品版本（按类型匹配，没有则为 null）
      const colVersions = cols.map((col) => this.getProdVersionByType(prod, col.type));

      // 按列模板解析 valueJson：valueJson 的 key 是 version.type
      const parseByCols = (valueJson) => {
        let parsed = {};
        try { parsed = JSON.parse(valueJson) || {}; } catch (e) { parsed = {}; }
        return colVersions.map((ver) => {
          if (!ver) return ""; // 该列无此版本，空占位
          const key = String(ver.type ?? "");
          return parsed[key] || "";
        });
      };

      const flatten = (nodes, level = 0) => {
        if (!Array.isArray(nodes) || !nodes.length) return;
        nodes.forEach((node) => {
          const title = this.getLocalizedField(node, "title");
          const valueJson = this.getLocalizedField(node, "valueJson");
          const values = parseByCols(valueJson);
          const hasChildren =
            Array.isArray(node.children) && node.children.length > 0;
          const isCategory = hasChildren && values.every((v) => !v);
          result.push({
            id: node.id,
            title,
            values: isCategory ? new Array(cols.length).fill("") : values,
            isCategory,
            level,
          });
          if (hasChildren) flatten(node.children, level + 1);
        });
      };
      flatten(comparisonList);
      return result;
    },

    // 版本标签样式（按版本类型区分颜色）
    versionTagClass(ver) {
      const t = Number(ver.type);
      if (t === 0) return "tag-free";      // 免费版
      if (t === 99) return "tag-custom";   // 定制版
      return "tag-subscribe";              // 订阅版
    },

    // 点击产品行右侧的版本标签 → 触发对应版本购买
    handleVersionBuy(ver, prod) {
      this.choosedSoft = prod;
      this.chooseFamily = this.productTypes.find(
        (f) => f.id === this.activeFamilyId
      ) || this.chooseFamily;
      this.handlePlanAction(ver);
    },

    // 点击版本卡片“免费试用”标签：对该版本走免费下单
    async handleProductCardFreeTrial(plan, prod) {
      if (prod) this.choosedSoft = prod;
      const isFree = plan && Number(plan.type) === PLAN_TYPE.FREE;
      if (!isFree) {
        this.$message.warning("当前产品暂无免费版");
        return;
      }
      this.toBuy(plan, "free");
    },

    async getProduct(familyCode = "SummitMap") {
      try {
        const res = await getAllProduction({
          familyCode,
          saletype: "online",
          enabledmark: 1,
          sidx: "sortcode",
          sort: "asc",
        });
        if (res.data?.list?.length === 0) {
          this.loopSoftList = [];
          this.choosedSoft = {};
          return;
        }
        this.loopSoftList = res.data?.list || [];
        this.selectProductCard(this.loopSoftList[0]);
        if (this.loopSoftList.length > 0) {
          this.choosedSoft = this.loopSoftList[0];
        }
      } catch (e) {
        console.error("获取产品列表失败:", e);
      }
    },

    async getProductionVersion(productId) {
      if (!productId) return;

      try {
        const res = await productionVersion({
          currentPage: 1,
          pageSize: 20,
          productId,
          enabledmark: 1,
        });

        if (res.code === 200 && res.data?.records) {
          this.pricingList = res.data.records.map((item) => ({
            ...item,
            icon: this.parseImageUrl(item.icon),
            img: this.parseImageUrl(item.img),
            features: this.parseJson(item.coreFuc),
            weekprice: Math.max(item.weekprice || 0, 0),
          }));

          if (this.pricingList.length > 0) {
            this.selectedPlan = this.pricingList[0].id;
          }
        } else {
          this.pricingList = [];
        }
      } catch (e) {
        console.error("获取定价方案失败:", e);
        this.pricingList = [];
      }
    },

    async getFamilyGetInfo(id) {
      if (!id) return;
      try {
        const res = await getTreeByProductId(id);
        if (res.code === 200 && res.data) {
          this.comparisonList = res.data;
        }
      } catch (e) {
        console.error("获取功能对比失败:", e);
      }
    },

    async getCommonQuestion() {
      if (!this.choosedSoft?.id) return;
      try {
        const res = await commonQuestion(this.choosedSoft.id);
        if (res.code === 200 && res.data?.length > 0) {
          this.faqList = res.data.map((item, index) => ({
            question: this.getLocalizedField(item, "title"),
            answer: this.getLocalizedField(item, "content"),
            expanded: false,
            iconBg: PRODUCT_COLORS[index % PRODUCT_COLORS.length],
            icon: "el-icon-question",
          }));
        }
      } catch (e) {
        console.error("获取常见问题失败:", e);
      }
    },

    async getFunctionDetail() {
      if (!this.choosedSoft?.id) return;

      const baseQuery = { productid: this.choosedSoft.id };

      try {
        const [detailRes, functionRes] = await Promise.all([
          getProductDetailList({
            ...baseQuery,
            sort: "desc",
            sidx: "sortcode",
          }),
          getProductFuntionList({
            ...baseQuery,
            sort: "asc",
            sidx: "sortcode",
            enabledmark: 1,
          }),
        ]);

        this.detailList = detailRes.data?.list || [];
        this.functionList = functionRes.data?.list || [];
        this.mergeRow(this.functionList, ["pointdata"]);
      } catch (e) {
        console.error("获取功能详情失败:", e);
      }
    },

    // ==================== 事件处理 ====================
    handleCategoryClick(tab) {
      this.currentCategory = tab.name;
      this.getFamily(tab.name === "all" ? null : tab.name);
    },

    handleProductClick(item) {
      this.chooseFamily = item;
      this.activeID = item.id;
      this.getProduct(item.familyCode);
      this.getrequireContentList(item.id);
    },

    // 点击上方产品卡片里的“立即购买”：先选中该产品，再滚动到下方购买区域
    handleProductCardBuy(item) {
      this.selectProductCard(item);

      this.$nextTick(() => {
        if (this.$refs.pricingSection) {
          this.$refs.pricingSection.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },

    selectProductCard(item) {
      this.currentProductId = item.id;
      this.chooseSoft(item);
      this.getFamilyGetInfo(item.id);
    },

    slideProductCards(direction) {
      if (this.slideAnimating) return;

      const isPrev = direction === "prev";
      const maxIndex = this.loopSoftList.length - 3;

      if (isPrev && this.currentIndex <= 0) return;
      if (!isPrev && this.currentIndex >= maxIndex) return;

      this.slideAnimating = true;
      this.currentIndex = isPrev ? this.currentIndex - 1 : this.currentIndex + 1;

      setTimeout(() => {
        this.slideAnimating = false;
      }, 350);
    },

    prevProductCard() {
      this.slideProductCards("prev");
    },

    nextProductCard() {
      this.slideProductCards("next");
    },

    chooseSoft(data) {
      if (!data) return;
      this.choosedSoft = data;
      this.getFunctionDetail();
      this.getCommonQuestion();
      this.getProductionVersion(data.id);
    },

    handleGuideSelect(id) {
      this.currentGuideId = id;
      this.fetchGuideDetail(id);
    },

    toggleFaq(index) {
      if (this.faqList[index]) {
        this.faqList[index].expanded = !this.faqList[index].expanded;
      }
    },

    // ==================== 订阅弹框相关 ====================
    handleSubscribeConfirm(data) {
      // 关闭订阅弹框后，打开订单确认弹框
      this.currentOrderInfos = [data];
      this.$nextTick(() => {
        this.orderConfirmDialogVisible = true;
      });
    },

    async handleAddCart(data) {
      if (!this.$store.state.user.isLogin) {
        this.$message.warning("请先登录");
        this.shoppingCartDialogVisible = false;
        this.$router.replace({
          name: "Login",
          path: "/login",
        });
        return;
      }

      // 校验用户信息是否有效
      const valid = await this.checkUserInfo();
      if (!valid) return;

      this.currentOrderInfo = data;
      this.$nextTick(() => {
        this.shoppingCartDialogVisible = true;
      });
    },
    // 购物车结算按钮
    async handleCartSettle(items) {
      if (!Array.isArray(items) || items.length === 0) {
        this.$message.warning(this.$t("Buy.selectProductFirst"));
        return;
      }

      // 校验用户信息是否有效
      const valid = await this.checkUserInfo();
      if (!valid) return;

      this.currentOrderInfos = items;
      this.shoppingCartDialogVisible = false;
      this.orderConfirmDialogVisible = true;
    },

    // ==================== 校验用户信息 ====================
    async checkUserInfo() {
      try {
        const res = await GetUserMesByToken();
        if (res.code !== 200 || !res.data) {
          this.$message.error("请重新登录");
          this.$router.replace({
            name: "Login",
            path: "/login",
          });
          return false;
        }
        return true;
      } catch (e) {
        this.$message.error("请重新登录");
        this.$router.replace({
          name: "Login",
          path: "/login",
        });
        return false;
      }
    },

    // ==================== 导航 ====================
    async toBuy(item, type) {
      console.log("item:",item);
      if (this.$store.state.user.isLogin) {
        // 校验用户信息是否有效
        const valid = await this.checkUserInfo();
        if (!valid) return;
        item.typeItem = this.choosedSoft || this.loopSoftList[0];
        item.chooseFamily = this.chooseFamily;
        item.isFree = type === "free";
        this.currentSubscribeProduct = item;
        this.subscribeDialogVisible = true;
      } else {
        this.$router.replace({
          name: "Login",
          path: "/login",
        });
      }
      // this.$router.push({ path: "/BuyMenu", query: { id } });
    },

    contactUs() {
      // 定制版“联系客服”与右侧悬浮“联系客服”保持一致：打开客服弹窗
      if (this.$refs.chatDialog) {
        this.$refs.chatDialog.open();
      }
    },

    goToDetail(item) {
      if (this.$refs.comparisonSection) {
        this.$refs.comparisonSection.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } else {
        console.warn("未找到功能对比区域");
      }
    },

    // ==================== 表格处理 ====================
    mergeRow(data, mergeFields) {
      if (!Array.isArray(data) || !Array.isArray(mergeFields)) return data;

      mergeFields.forEach((field) => {
        const mergeInfo = {};
        data.forEach((row, index) => {
          const value = row[field];
          const spanKey = `${field}-span`;

          if (mergeInfo[value]?.newIndex === index) {
            mergeInfo[value].num++;
            mergeInfo[value].newIndex++;
            data[mergeInfo[value].index][spanKey].rowspan++;
            row[spanKey] = { rowspan: 0, colspan: 0 };
          } else {
            mergeInfo[value] = { num: 1, index, newIndex: index + 1 };
            row[spanKey] = { rowspan: 1, colspan: 1 };
          }
        });
      });

      return data;
    },

    arraySpanMethod({ row, column }) {
      const spanKey = `${column.property}-span`;
      return row[spanKey] || null;
    },
  },
};
</script>

<style scoped lang="scss">
@import "./css/buy.scss";
</style>
